import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable } from 'rxjs';
import { LookupOption, LookupTypeCodes } from '../../../models/lookup.models';
import {
  ResourceAttributeValueDto,
  ResourceAttributesDto,
  ResourceTagCategory,
  ResourceTagDto,
} from '../../../models/resource.models';
import { LookupService } from '../../../services/lookup.service';
import { ResourceService } from '../../../services/resource.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { describeApiError } from '../../../utils/api-error.utils';

/** One attribute category: which DTO list it reads, which API tag category it
 *  writes, which lookup type feeds its options, and how it is labelled. */
interface AttributeCategory {
  key: keyof ResourceAttributesDto;
  category: ResourceTagCategory;
  label: string;
  typeCode: string;
}

/**
 * Section for a resource's profile attributes (Skills, Jurisdictions, Languages,
 * Interests, Sub-Practice Areas). Used on the User Profile page in self mode
 * (my/ endpoints, no special permission) and on Resource Details in resource
 * mode (gated by Resources.Write).
 *
 * Tags are edited inline, Azure DevOps-style: each row shows its tags with an
 * "x" to remove, plus a "+" (or "Add <category>" when empty) that opens an
 * inline input with typeahead suggestions; Enter creates a brand-new tag when
 * nothing matches (the API creates the tag value on demand). Every add/remove
 * persists immediately via the per-tag POST/DELETE endpoints — there is no
 * edit mode or unsaved-changes state.
 */
@Component({
  selector: 'app-resource-attributes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './resource-attributes.component.html',
  styleUrls: ['./resource-attributes.component.scss'],
})
export class ResourceAttributesComponent implements OnInit {
  private resourceService = inject(ResourceService);
  private lookupService = inject(LookupService);
  private snackbarService = inject(SnackbarService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  /** Employee ID of the resource to manage; ignored in self mode. */
  readonly employeeId = input<string>('');
  /** When true, loads/saves the current user's own attributes via the my/ endpoints. */
  readonly selfMode = input<boolean>(false);
  readonly isReadOnly = input<boolean>(false);

  /** The inline tag input (present only while a row is in add mode). */
  private readonly tagInputEl =
    viewChild<ElementRef<HTMLInputElement>>('tagInputEl');

  readonly categories: AttributeCategory[] = [
    { key: 'skills', category: 'Skill', label: 'Skills', typeCode: LookupTypeCodes.SKILLS },
    { key: 'jurisdictions', category: 'Jurisdiction', label: 'Jurisdictions', typeCode: LookupTypeCodes.JURISDICTIONS },
    { key: 'languages', category: 'Language', label: 'Languages', typeCode: LookupTypeCodes.LANGUAGES },
    { key: 'interests', category: 'Interest', label: 'Interests', typeCode: LookupTypeCodes.INTERESTS },
    { key: 'subPracticeAreas', category: 'SubPractice', label: 'Sub-Practice Areas', typeCode: LookupTypeCodes.SUB_PRACTICE },
  ];

  attributes: ResourceAttributesDto | null = null;
  optionsByCategory: Record<string, LookupOption[]> = {};

  isLoading = false;

  /** Category key whose inline add input is currently open (one at a time). */
  addingCategoryKey: string | null = null;
  tagInputText = '';
  /** True while an add/remove/create request is in flight; actions are ignored. */
  isSavingTag = false;

  /**
   * Set when the inline input lost focus while a save was in flight. The blur is
   * deferred rather than dropped, so the save's completion closes the input
   * instead of re-focusing it and yanking focus out of whatever the user clicked.
   */
  private pendingCloseAdd = false;

  /**
   * Sequence number for attribute loads. The target resource can change while a
   * load is in flight (e.g. the floating dialog is reused), so a response from
   * anything but the latest request is discarded.
   */
  private loadSequence = 0;

  constructor() {
    // Reload whenever the target resource changes (e.g. floating dialog reuse).
    effect(() => {
      this.employeeId();
      this.selfMode();
      this.loadAttributes();
    });
  }

  ngOnInit(): void {
    for (const category of this.categories) {
      this.lookupService
        .getLookupOptions(category.typeCode)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (options) => {
            this.optionsByCategory[category.key] = options;
            this.cdr.markForCheck();
          },
          error: () => {
            this.optionsByCategory[category.key] = [];
          },
        });
    }
  }

  getValues(category: AttributeCategory): ResourceAttributeValueDto[] {
    return this.attributes?.[category.key] ?? [];
  }

  /** Total assigned values across all categories, shown next to the title. */
  get totalCount(): number {
    return this.categories.reduce(
      (sum, category) => sum + this.getValues(category).length,
      0,
    );
  }

  /**
   * Typeahead suggestions for the category's inline input: active lookup values
   * not already assigned, filtered by the typed text (case-insensitive contains).
   */
  getSuggestions(category: AttributeCategory): LookupOption[] {
    const assigned = new Set(this.getValues(category).map((v) => v.code));
    const search = this.tagInputText.trim().toLowerCase();
    return (this.optionsByCategory[category.key] ?? []).filter(
      (o) =>
        !assigned.has(o.value) &&
        (!search || o.label.toLowerCase().includes(search)),
    );
  }

  /**
   * True when the typed text matches no existing option label exactly, so Enter
   * would create a brand-new tag (drives the "Create ..." hint row).
   */
  isNewTagText(category: AttributeCategory): boolean {
    const text = this.tagInputText.trim().toLowerCase();
    if (!text) {
      return false;
    }
    return !(this.optionsByCategory[category.key] ?? []).some(
      (o) => o.label.toLowerCase() === text,
    );
  }

  /** Opens the inline add input for the category (one at a time). */
  startAdd(category: AttributeCategory): void {
    if (this.isReadOnly() || this.isSavingTag) {
      return;
    }
    this.addingCategoryKey = category.key;
    this.tagInputText = '';
    this.pendingCloseAdd = false;
    this.cdr.detectChanges();
    this.tagInputEl()?.nativeElement.focus();
  }

  closeAdd(): void {
    this.addingCategoryKey = null;
    this.tagInputText = '';
    this.pendingCloseAdd = false;
  }

  /**
   * Closes the input on blur. While a save is mid-flight the input stays open so
   * its spinner remains visible, but the blur is remembered rather than dropped
   * and honoured once the request settles (see finishSave).
   */
  onInputBlur(): void {
    if (this.isSavingTag) {
      this.pendingCloseAdd = true;
      return;
    }
    this.closeAdd();
  }

  /**
   * Clears the in-flight flag and settles a blur that arrived mid-save, running
   * the caller's follow-up (which re-focuses the input for rapid multi-add) only
   * when the input still holds focus.
   */
  private finishSave(onSettled?: () => void): void {
    this.isSavingTag = false;

    if (this.pendingCloseAdd) {
      this.closeAdd();
      this.cdr.detectChanges();
      return;
    }

    this.cdr.detectChanges();
    onSettled?.();
  }

  /** Adds an existing lookup value (suggestion click) and persists immediately. */
  addExisting(category: AttributeCategory, option: LookupOption): void {
    if (this.isSavingTag) {
      return;
    }
    const codes = this.getValues(category).map((v) => v.code);
    if (codes.includes(option.value)) {
      this.tagInputText = '';
      return;
    }
    // The API matches by normalized name, so sending the label re-resolves to
    // the same lookup value the option represents.
    this.persistAdd(category, option.label, () => {
      // Keep the input open for rapid multi-add; just clear the text.
      this.tagInputText = '';
      this.tagInputEl()?.nativeElement.focus();
    });
  }

  /**
   * Enter in the inline input: adds the exact (case-insensitive) matching
   * existing value, or creates a brand-new tag and assigns it (the API
   * find-or-creates the tag value by name in the same call).
   */
  onInputEnter(category: AttributeCategory): void {
    const text = this.tagInputText.trim();
    if (this.isSavingTag) {
      return;
    }
    if (!text) {
      this.closeAdd();
      return;
    }

    const match = (this.optionsByCategory[category.key] ?? []).find(
      (o) => o.label.toLowerCase() === text.toLowerCase(),
    );
    if (match) {
      this.addExisting(category, match);
      return;
    }

    this.persistAdd(category, text, () => {
      this.tagInputText = '';
      this.tagInputEl()?.nativeElement.focus();
    });
  }

  /** Removes a tag and persists immediately via the per-tag delete endpoint. */
  removeTag(category: AttributeCategory, value: ResourceAttributeValueDto): void {
    if (this.isReadOnly() || this.isSavingTag) {
      return;
    }

    this.isSavingTag = true;
    const request$: Observable<void> = this.selfMode()
      ? this.resourceService.removeMyTag(value.lookupValueId)
      : this.resourceService.removeResourceTag(
          this.employeeId(),
          value.lookupValueId,
        );

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (this.attributes) {
          this.attributes[category.key] = this.getValues(category).filter(
            (v) => v.lookupValueId !== value.lookupValueId,
          );
        }
        this.finishSave();
      },
      error: (error: Error) => {
        this.finishSave();
        this.snackbarService.showError(
          describeApiError(error, 'Failed to save profile attributes'),
        );
      },
    });
  }

  private loadAttributes(): void {
    // Invalidate any in-flight load: its response is for the previous resource.
    const sequence = ++this.loadSequence;

    if (!this.selfMode() && !this.employeeId()) {
      this.attributes = null;
      return;
    }

    this.isLoading = true;
    const request$: Observable<ResourceAttributesDto> = this.selfMode()
      ? this.resourceService.getMyAttributes()
      : this.resourceService.getResourceAttributes(this.employeeId());

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (attributes) => {
        if (sequence !== this.loadSequence) {
          return;
        }
        this.attributes = attributes;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        if (sequence !== this.loadSequence) {
          return;
        }
        this.attributes = null;
        this.isLoading = false;
        this.snackbarService.showError('Failed to load profile attributes');
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Adds one tag by name via the per-tag POST (the API find-or-creates the tag
   * value), then updates local state from the returned canonical tag. Local
   * state only changes on success, so no revert is needed on error.
   */
  private persistAdd(
    category: AttributeCategory,
    tagName: string,
    onSuccess?: () => void,
  ): void {
    this.isSavingTag = true;
    const request$: Observable<ResourceTagDto> = this.selfMode()
      ? this.resourceService.addMyTag(category.category, tagName)
      : this.resourceService.addResourceTag(
          this.employeeId(),
          category.category,
          tagName,
        );

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (tag) => {
        // Guard the load-failed case: a successful add still has a home.
        this.attributes ??= {
          skills: [],
          jurisdictions: [],
          languages: [],
          interests: [],
          subPracticeAreas: [],
        };

        // The add is idempotent server-side; dedupe by code locally too.
        if (!this.getValues(category).some((v) => v.code === tag.code)) {
          this.attributes[category.key] = [
            ...this.getValues(category),
            { code: tag.code, name: tag.name, lookupValueId: tag.lookupValueId },
          ];
        }

        const options = this.optionsByCategory[category.key] ?? [];
        if (!options.some((o) => o.value === tag.code)) {
          // Prepend so the new value sits at the top of future suggestion lists.
          this.optionsByCategory[category.key] = [
            { value: tag.code, label: tag.name },
            ...options,
          ];
          // Other consumers cache this lookup type; drop the cached copy so a
          // brand-new tag shows up there too.
          this.lookupService.clearCache(category.typeCode);
        }

        this.finishSave(onSuccess);
      },
      error: (error: Error) => {
        this.finishSave();
        this.snackbarService.showError(
          describeApiError(error, 'Failed to save profile attributes'),
        );
      },
    });
  }
}
